/**
 * SharedPlan — Public, read-only view of a shared itinerary.
 *
 * Route: /p/:slug  (marketing / public zone — no auth)
 *
 * Renders a plan that its owner has explicitly shared. The backend only
 * returns plans with is_shared = true; anything else 404s, and this page
 * shows a friendly not-found state in that case.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  alpha,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  LocationOn,
  CalendarMonth,
  AccessTime,
  Flight,
  Hotel,
  DirectionsCar,
  Restaurant,
  Hiking,
  MoreHoriz,
  Public,
  Explore,
  Send,
  Groups,
  CheckCircle,
} from '@mui/icons-material';
import plansService from '../../services/plansService';
import ButtonPill from '../ui/ButtonPill';

const ITEM_TYPE_ICONS = {
  flight: Flight,
  hotel: Hotel,
  activity: Hiking,
  meal: Restaurant,
  transport: DirectionsCar,
  custom: MoreHoriz,
};

const ITEM_TYPE_COLORS = {
  flight: '#1976D2',
  hotel: '#7B1FA2',
  activity: '#2E7D32',
  meal: '#E65100',
  transport: '#0097A7',
  custom: '#616161',
};

const ITEM_TYPE_LABELS = {
  flight: 'Flight',
  hotel: 'Hotel',
  activity: 'Activity',
  meal: 'Meal',
  transport: 'Transport',
  custom: 'Custom',
};

const formatCurrency = (value, currency = 'USD') => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num)
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
};

const SharedPlan = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Guest enquiry ("Request this itinerary") — no account required.
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: '', email: '', phone: '', num_travelers: 2, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [enquiryDone, setEnquiryDone] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadPlan = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const res = await plansService.getSharedPlan(slug);
      if (!res?.plan) {
        setNotFound(true);
        return;
      }
      setPlan(res.plan);
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const currency = plan?.currency || 'USD';
  const days = useMemo(() => plan?.plan_days ?? [], [plan]);

  const handleEnquirySubmit = async () => {
    if (!enquiry.name.trim() || !enquiry.email.trim()) {
      setSnackbar({ open: true, message: 'Please add your name and email.', severity: 'error' });
      return;
    }
    try {
      setSubmitting(true);
      await plansService.requestSharedPlan(slug, {
        guest_name: enquiry.name,
        guest_email: enquiry.email,
        guest_phone: enquiry.phone || null,
        num_travelers: parseInt(enquiry.num_travelers, 10) || 1,
        message: enquiry.message || null,
      });
      setEnquiryDone(true);
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Could not send your enquiry. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (notFound || !plan) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Public sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          This plan is not available
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 420 }}>
          The link may be incorrect, or the owner has turned off sharing for this itinerary.
        </Typography>
        <ButtonPill variant="contained" startIcon={<Explore />} onClick={() => navigate('/trips')}>
          Explore Trips
        </ButtonPill>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', pb: 8 }}>
      {/* ═══ Hero ═══ */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 8 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.12)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Chip
            icon={<Public sx={{ fontSize: 16, color: 'inherit !important' }} />}
            label="Shared itinerary"
            size="small"
            sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
          />
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontFamily: '"Playfair Display", serif',
              textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
            }}
          >
            {plan.title || 'Trip Itinerary'}
          </Typography>
          {plan.creator_first_name && (
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mt: 1 }}>
              Curated by {plan.creator_first_name}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
            {plan.location && (
              <Chip
                icon={<LocationOn />}
                label={plan.location}
                sx={{ bgcolor: 'rgba(255,255,255,0.92)', color: theme.palette.primary.dark, fontWeight: 600 }}
              />
            )}
            {plan.duration && (
              <Chip
                icon={<CalendarMonth />}
                label={plan.duration}
                sx={{ bgcolor: 'rgba(255,255,255,0.92)', color: theme.palette.primary.dark, fontWeight: 600 }}
              />
            )}
            <Chip
              label={`${plan.days_count ?? days.length} day${(plan.days_count ?? days.length) !== 1 ? 's' : ''} · ${plan.items_count ?? 0} item${(plan.items_count ?? 0) !== 1 ? 's' : ''}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.92)', color: theme.palette.text.secondary, fontWeight: 600 }}
            />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* ═══ Overview ═══ */}
        {(plan.description || plan.calculated_total > 0) && (
          <Paper
            elevation={0}
            sx={{ p: { xs: 2.5, md: 4 }, mb: 4, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}` }}
          >
            {plan.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: plan.calculated_total > 0 ? 3 : 0, lineHeight: 1.7 }}>
                {plan.description}
              </Typography>
            )}
            {plan.calculated_total > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Estimated total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {formatCurrency(plan.calculated_total, currency)}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* ═══ Day-by-Day Itinerary ═══ */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: theme.palette.primary.main }}>
          Day-by-Day Itinerary
        </Typography>

        <Stack spacing={2.5}>
          {days.map((day, dayIndex) => {
            const items = day.plan_items || [];
            const dayCost = items.reduce((s, i) => s + (parseFloat(i.cost) || 0), 0);
            return (
              <Paper
                key={day.id ?? `day-${dayIndex}`}
                elevation={0}
                sx={{ borderRadius: 3, border: `2px solid ${theme.palette.grey[200]}`, overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip label={`Day ${day.day || dayIndex + 1}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {day.title || `Day ${day.day || dayIndex + 1}`}
                    </Typography>
                  </Box>
                  {dayCost > 0 && (
                    <Chip
                      label={formatCurrency(dayCost, currency)}
                      size="small"
                      variant="outlined"
                      sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
                    />
                  )}
                </Box>

                <CardContent sx={{ pt: 2.5 }}>
                  {day.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                      {day.notes}
                    </Typography>
                  )}

                  {items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No items planned for this day.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {items.map((item, itemIndex) => {
                        const ItemIcon = ITEM_TYPE_ICONS[item.item_type] || MoreHoriz;
                        const itemColor = ITEM_TYPE_COLORS[item.item_type] || theme.palette.text.secondary;
                        return (
                          <Card
                            key={item.id ?? `item-${itemIndex}`}
                            variant="outlined"
                            sx={{ borderRadius: 2, borderLeft: `4px solid ${itemColor}` }}
                          >
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(itemColor, 0.1),
                                    flexShrink: 0,
                                  }}
                                >
                                  <ItemIcon sx={{ color: itemColor, fontSize: 18 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {item.name || ITEM_TYPE_LABELS[item.item_type] || 'Item'}
                                    </Typography>
                                    {parseFloat(item.cost) > 0 && (
                                      <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, flexShrink: 0 }}>
                                        {formatCurrency(item.cost, currency)}
                                      </Typography>
                                    )}
                                  </Box>
                                  {item.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                                      {item.description}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                                    {item.location && (
                                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: theme.palette.text.secondary }}>
                                        <LocationOn sx={{ fontSize: 13 }} />
                                        {item.location}
                                      </Typography>
                                    )}
                                    {(item.start_time || item.end_time) && (
                                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: theme.palette.text.secondary }}>
                                        <AccessTime sx={{ fontSize: 13 }} />
                                        {item.start_time || '?'}
                                        {item.end_time ? ` – ${item.end_time}` : ''}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Paper>
            );
          })}
        </Stack>

        {/* ═══ CTA ═══ */}
        <Paper
          elevation={0}
          sx={{
            mt: 5,
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            border: `2px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Love this itinerary?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
            Request this exact trip and our team will put together a custom quote for you. No account
            needed, and nothing is booked until you confirm.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <ButtonPill variant="contained" startIcon={<Send />} onClick={() => { setEnquiryDone(false); setEnquiryOpen(true); }}>
              Request this itinerary
            </ButtonPill>
            <ButtonPill variant="outlined" startIcon={<Explore />} onClick={() => navigate('/trips')}>
              Explore Trips
            </ButtonPill>
          </Stack>
        </Paper>
      </Container>

      {/* ═══ Guest enquiry dialog ═══ */}
      <Dialog
        open={enquiryOpen}
        onClose={() => !submitting && setEnquiryOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {enquiryDone ? (
          <DialogContent sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Enquiry sent
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
              Thanks! Our team has your details and this itinerary, and will reach out with a
              custom quote. No payment is due yet.
            </Typography>
            <ButtonPill variant="contained" onClick={() => setEnquiryOpen(false)}>
              Done
            </ButtonPill>
          </DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>Request this itinerary</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Tell us how to reach you and our team will send a free, custom quote for
                "{plan?.title}". No account needed.
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Your name"
                    value={enquiry.name}
                    onChange={(e) => setEnquiry((f) => ({ ...f, name: e.target.value }))}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    type="email"
                    value={enquiry.email}
                    onChange={(e) => setEnquiry((f) => ({ ...f, email: e.target.value }))}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone (optional)"
                    value={enquiry.phone}
                    onChange={(e) => setEnquiry((f) => ({ ...f, phone: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Number of travelers"
                    type="number"
                    value={enquiry.num_travelers}
                    onChange={(e) => setEnquiry((f) => ({ ...f, num_travelers: e.target.value }))}
                    fullWidth
                    inputProps={{ min: 1 }}
                    InputProps={{
                      startAdornment: (
                        <Groups sx={{ fontSize: 20, mr: 0.75, color: theme.palette.text.secondary }} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Message (optional)"
                    value={enquiry.message}
                    onChange={(e) => setEnquiry((f) => ({ ...f, message: e.target.value }))}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Preferred dates, special requests, questions..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setEnquiryOpen(false)} disabled={submitting} sx={{ borderRadius: 9999 }}>
                Cancel
              </Button>
              <ButtonPill
                variant="contained"
                onClick={handleEnquirySubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} /> : <Send />}
              >
                {submitting ? 'Sending...' : 'Send Enquiry'}
              </ButtonPill>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SharedPlan;
